// @flow

import React, { useCallback, useMemo, useRef } from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { absolute, Color, flex, hoverChild, Layout } from 'styles';
import { useModalDispatch, useModalState, ModalContextActions } from 'contexts';
import { useNavState } from 'contexts';
import { Close } from 'components/Svg';
import { Link } from 'components/Gui';
import { ESC } from 'Constants';

const DesktopWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: ${Layout.Z_INDEX_MODAL};
  transform: ${({ visible }) => (visible ? `scale(1)` : `scale(0)`)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: ${({ visible }) => (visible ? `opacity 300ms linear` : `opacity 200ms linear, transform 0ms linear 200ms`)};
  ${flex()};

  > div {
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    transition: ${({ visible }) => (visible ? `opacity 0ms linear 300ms` : `opacity 200ms linear`)};
    > div {
      border-radius: 4px;
      position: relative;
      background: ${Color.WHITE};
      opacity: ${({ visible }) => (visible ? 1 : 0)};
      transform: ${({ visible }) => (visible ? 'translate(10px, -10px)' : 'none')};
      transition: ${({ visible }) => (visible ? `transform 500ms ease 300ms, opacity 500ms ease 300ms` : `none`)};
    }
  }
`;

const CloseButton = styled(Link)`
  cursor: pointer;
  ${absolute('18px', '18px', 'unset', 'unset')};
  ${flex()};
  width: 32px;
  height: 32px;
  ${hoverChild('line', 'stroke', Color.BLACK_SOUL, Color.PURPLE_MAIN)};

  svg {
    width: 16px;
  }
`;

const MobileWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${Color.WHITE};
  z-index: ${Layout.Z_INDEX_MODAL};
  transition: ${({ visible }) => (visible ? `transform 400ms linear` : `transform 0ms linear 10ms`)};
  transform: translateY(${({ visible }) => (visible ? 0 : '-100%')});
  overflow-y: auto;
  overflow-scrolling: touch;
`;

type Props = {
  id: string,
  children: Node,
};

const Modal = ({ id: modalId, children }: Props) => {
  const { renderDesktop, renderMobile } = useNavState();
  const { isOpen, id } = useModalState();
  const dispatch = useModalDispatch();
  const ref = useRef(null);
  const closeOnClick = useCallback(() => {
    dispatch({
      type: ModalContextActions.CLOSE,
    });
  }, [dispatch]);
  const closeOnEsc = useCallback(
    ({ key, keyCode }) => {
      if (ESC.includes(key || keyCode)) {
        dispatch({
          type: ModalContextActions.CLOSE,
        });
      }
    },
    [dispatch]
  );
  const showing = isOpen && modalId === id;
  return useMemo(
    () => (
      <>
        {renderDesktop && (
          <DesktopWrapper visible={showing} role="dialog" onKeyDown={closeOnEsc} onClick={closeOnClick}>
            {/* eslint-disable-next-line */}
            <div onClick={e => e.stopPropagation()}>
              <div ref={ref}>
                {showing && children}
                <CloseButton label="Close the modal." onClick={closeOnClick}>
                  {Close}
                </CloseButton>
              </div>
            </div>
          </DesktopWrapper>
        )}
        {renderMobile && <MobileWrapper visible={showing}>{children}</MobileWrapper>}
      </>
    ),
    [showing, children, renderDesktop, renderMobile, closeOnClick, closeOnEsc]
  );
};

export default Modal;
